import React, { useState, useEffect, useMemo } from 'react'
import {
  ArrowRightLeft,
  Calculator,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Plus,
  Trash2,
  Info,
  TrendingUp
} from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { useToastHelpers } from '../../contexts/ToastContext'
import {
  Exchange1031Analysis,
  Exchange1031Requirements,
  LikeKindProperty,
  UserTaxProfile
} from '../../types/tax-analysis'
import { PropertyResponse } from '../../services/api'

interface Exchange1031CalculatorProps {
  property: PropertyResponse
  taxProfile?: UserTaxProfile | null
  loading?: boolean
  onAnalysisChange?: (analysis: Exchange1031Analysis) => void
}

interface ExchangeFormState {
  current_market_value: string
  original_purchase_price: string
  total_depreciation_taken: string
  improvement_costs: string
  selling_costs_percentage: string
  replacement_properties: LikeKindProperty[]
  errors: Record<string, string>
  isCalculating: boolean
}

const CAPITAL_GAINS_RATES = {
  short_term: 0.37, // Ordinary income rates up to 37%
  long_term_0: 0.0,  // 0% for lower income brackets
  long_term_15: 0.15, // 15% for middle income brackets
  long_term_20: 0.20, // 20% for high income brackets
  depreciation_recapture: 0.25, // 25% on depreciation recapture
  net_investment_income: 0.038 // 3.8% NIIT for high earners
}

const EXCHANGE_DEADLINES = {
  identification_period: 45,
  exchange_period: 180
}

export function Exchange1031Calculator({
  property,
  taxProfile,
  loading = false,
  onAnalysisChange
}: Exchange1031CalculatorProps) {
  const { showSuccess, showError, showWarning } = useToastHelpers()
  const [formState, setFormState] = useState<ExchangeFormState>({
    current_market_value: '',
    original_purchase_price: '',
    total_depreciation_taken: '',
    improvement_costs: '',
    selling_costs_percentage: '6',
    replacement_properties: [],
    errors: {},
    isCalculating: false
  })

  const [exchangeAnalysis, setExchangeAnalysis] = useState<Exchange1031Analysis | null>(null)
  const [showRequirements, setShowRequirements] = useState(false)

  // Initialize form with property data
  useEffect(() => {
    if (property && property.purchase_price) {
      // Safely handle null/undefined values with fallbacks
      const purchasePrice = property.purchase_price || 0
      const estimatedValue = purchasePrice * 1.1

      // Calculate estimated depreciation (simplified) - handle null created_at
      const createdDate = property.created_at ? new Date(property.created_at) : new Date()
      const yearsOwned = Math.max(1, new Date().getFullYear() - createdDate.getFullYear())
      const depreciableBasis = purchasePrice * 0.8 // Assume 20% land value
      const estimatedDepreciation = (depreciableBasis / 27.5) * yearsOwned

      setFormState(prev => {
        // Only update if values are actually different to prevent loops
        const newState = {
          ...prev,
          current_market_value: estimatedValue.toString(),
          original_purchase_price: purchasePrice.toString(),
          total_depreciation_taken: Math.min(estimatedDepreciation, depreciableBasis).toString(),
          improvement_costs: '0'
        }

        // Check if state would actually change before updating
        if (prev.current_market_value !== newState.current_market_value ||
            prev.original_purchase_price !== newState.original_purchase_price ||
            prev.total_depreciation_taken !== newState.total_depreciation_taken ||
            prev.improvement_costs !== newState.improvement_costs) {
          return newState
        }
        return prev
      })
    }
  }, [property?.property_id, property?.purchase_price, property?.created_at]) // Only depend on specific property fields

  // Calculate 1031 analysis when form changes
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    // Debounce calculations to prevent excessive recalculation
    if (validateBasicInputs() && taxProfile && !formState.isCalculating) {
      timeoutId = setTimeout(() => {
        calculateExchangeAnalysis()
      }, 300) // 300ms debounce
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [
    formState.current_market_value,
    formState.original_purchase_price,
    formState.total_depreciation_taken,
    formState.improvement_costs,
    formState.selling_costs_percentage,
    taxProfile?.agi_estimate,
    taxProfile?.state_tax_rate
  ]) // Only depend on specific fields that affect calculations

  const validateBasicInputs = (): boolean => {
    const marketValue = parseFloat(formState.current_market_value || '0')
    const purchasePrice = parseFloat(formState.original_purchase_price || '0')
    const depreciation = parseFloat(formState.total_depreciation_taken || '0')

    return !isNaN(marketValue) && marketValue > 0 &&
           !isNaN(purchasePrice) && purchasePrice > 0 &&
           !isNaN(depreciation) && depreciation >= 0
  }

  const calculateExchangeAnalysis = async () => {
    if (!taxProfile || !validateBasicInputs()) {
      setExchangeAnalysis(null) // Clear previous analysis if inputs are invalid
      return
    }

    // Prevent multiple concurrent calculations
    if (formState.isCalculating) return

    setFormState(prev => ({ ...prev, isCalculating: true }))

    try {
      const analysis = await performExchangeCalculations()
      setExchangeAnalysis(analysis)

      if (onAnalysisChange) {
        onAnalysisChange(analysis)
      }
    } catch (error) {
      console.error('1031 Exchange calculation error:', error)
      showError('Calculation Error', 'Failed to calculate 1031 exchange analysis. Please check your input values.')
      setExchangeAnalysis(null) // Clear analysis on error
    } finally {
      setFormState(prev => ({ ...prev, isCalculating: false }))
    }
  }

  const performExchangeCalculations = async (): Promise<Exchange1031Analysis> => {
    // Add null safety with fallback values
    const currentValue = parseFloat(formState.current_market_value || '0') || 0
    const originalPrice = parseFloat(formState.original_purchase_price || '0') || 0
    const depreciationTaken = parseFloat(formState.total_depreciation_taken || '0') || 0
    const improvements = parseFloat(formState.improvement_costs || '0') || 0
    const sellingCostsRate = (parseFloat(formState.selling_costs_percentage || '6') || 6) / 100

    // Validate that we have reasonable values to work with
    if (currentValue <= 0 || originalPrice <= 0) {
      throw new Error('Invalid property values - market value and purchase price must be greater than zero')
    }

    // Calculate adjusted basis
    const adjustedBasis = Math.max(0, originalPrice + improvements - depreciationTaken)

    // Calculate gains
    const grossSalePrice = currentValue
    const sellingCosts = grossSalePrice * sellingCostsRate
    const netSaleProceeds = Math.max(0, grossSalePrice - sellingCosts)
    const totalGain = Math.max(0, netSaleProceeds - adjustedBasis)

    // Separate capital gain and depreciation recapture
    const depreciationRecapture = Math.min(depreciationTaken, Math.max(0, totalGain))
    const capitalGain = Math.max(0, totalGain - depreciationRecapture)

    // Calculate tax rates based on income level and profile - add null safety
    const agiEstimate = (taxProfile?.agi_estimate && !isNaN(taxProfile.agi_estimate)) ? taxProfile.agi_estimate : 100000
    const stateRate = (taxProfile?.state_tax_rate && !isNaN(taxProfile.state_tax_rate)) ? taxProfile.state_tax_rate : 0

    const federalCapGainsRate = determineFederalCapitalGainsRate(agiEstimate)
    const stateCapGainsRate = stateRate
    const isHighEarner = agiEstimate > 200000 // Simplified NIIT threshold

    // Calculate taxes
    const federalCapGainsTax = Math.max(0, capitalGain * federalCapGainsRate)
    const stateCapGainsTax = Math.max(0, capitalGain * stateCapGainsRate)
    const depreciationRecaptureTax = Math.max(0, depreciationRecapture * CAPITAL_GAINS_RATES.depreciation_recapture)
    const niitTax = isHighEarner ? Math.max(0, (capitalGain + depreciationRecapture) * CAPITAL_GAINS_RATES.net_investment_income) : 0

    const totalTaxLiability = federalCapGainsTax + stateCapGainsTax + depreciationRecaptureTax + niitTax

    // Calculate potential qualified replacement properties
    const minReplacementValue = netSaleProceeds // Must be equal or greater value
    const qualifiedProperties = (formState.replacement_properties || []).filter(prop =>
      prop && prop.market_value >= minReplacementValue && prop.qualification_status === 'qualified'
    )

    // Ensure all values are finite numbers to prevent rendering issues
    const safeValue = (val: number) => isFinite(val) ? val : 0

    return {
      property_id: property?.property_id || 'temp',
      current_market_value: safeValue(currentValue),
      adjusted_basis: safeValue(adjustedBasis),
      potential_capital_gain: safeValue(capitalGain),
      depreciation_recapture: safeValue(depreciationRecapture),
      federal_capital_gains_rate: safeValue(federalCapGainsRate),
      state_capital_gains_rate: safeValue(stateCapGainsRate),
      net_investment_income_tax: safeValue(niitTax),
      total_tax_liability: safeValue(totalTaxLiability),
      tax_deferred_via_1031: safeValue(totalTaxLiability), // Amount that can be deferred
      exchange_requirements: {
        identification_period_days: EXCHANGE_DEADLINES.identification_period,
        exchange_period_days: EXCHANGE_DEADLINES.exchange_period,
        qualified_intermediary_required: true,
        like_kind_requirement: 'Real property held for investment or business use',
        equal_or_greater_value: true,
        debt_replacement_rule: 'Replacement property debt must equal or exceed relinquished property debt'
      },
      like_kind_properties: qualifiedProperties
    }
  }

  const determineFederalCapitalGainsRate = (agi: number): number => {
    // Simplified 2024 capital gains brackets (single filer)
    if (agi <= 44625) return CAPITAL_GAINS_RATES.long_term_0
    if (agi <= 492300) return CAPITAL_GAINS_RATES.long_term_15
    return CAPITAL_GAINS_RATES.long_term_20
  }

  const updateField = (field: keyof ExchangeFormState, value: any) => {
    setFormState(prev => {
      // Clear errors for the field being updated
      const newErrors = { ...prev.errors }
      delete newErrors[field]

      // Validate numeric fields
      if ((field === 'current_market_value' || field === 'original_purchase_price') && value) {
        const numValue = parseFloat(value)
        if (isNaN(numValue) || numValue <= 0) {
          newErrors[field] = 'Must be a positive number'
        }
      }

      return {
        ...prev,
        [field]: value,
        errors: newErrors
      }
    })
  }

  const addReplacementProperty = () => {
    const newProperty: LikeKindProperty = {
      address: '',
      market_value: 0,
      property_type: 'residential',
      qualification_status: 'pending_review',
      qualification_notes: ''
    }

    setFormState(prev => ({
      ...prev,
      replacement_properties: [...prev.replacement_properties, newProperty]
    }))
  }

  const updateReplacementProperty = (index: number, field: keyof LikeKindProperty, value: any) => {
    setFormState(prev => {
      if (!prev.replacement_properties || index < 0 || index >= prev.replacement_properties.length) {
        return prev
      }

      return {
        ...prev,
        replacement_properties: prev.replacement_properties.map((prop, i) =>
          i === index ? { ...prop, [field]: value } : prop
        )
      }
    })
  }

  const removeReplacementProperty = (index: number) => {
    setFormState(prev => ({
      ...prev,
      replacement_properties: prev.replacement_properties.filter((_, i) => i !== index)
    }))
  }

  const getQualificationColor = (status: LikeKindProperty['qualification_status']) => {
    switch (status) {
      case 'qualified': return 'text-green-600 bg-green-100'
      case 'not_qualified': return 'text-red-600 bg-red-100'
      default: return 'text-yellow-600 bg-yellow-100'
    }
  }

  const exchangeBenefitPercentage = exchangeAnalysis && exchangeAnalysis.current_market_value > 0
    ? (exchangeAnalysis.tax_deferred_via_1031 / exchangeAnalysis.current_market_value) * 100
    : 0

  // Show loading state for initial load or calculations
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Loading property data...</span>
        </div>
      </div>
    )
  }

  if (!taxProfile) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <Calculator className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Tax Profile Required</h3>
          <p className="text-gray-600">
            Tax profile is needed to calculate 1031 exchange tax benefits
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <ArrowRightLeft className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  1031 Like-Kind Exchange Calculator
                </h3>
                <p className="text-sm text-gray-600">
                  Analyze tax deferment benefits and exchange requirements
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowRequirements(!showRequirements)}
              className="flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Requirements
            </Button>
          </div>
        </div>

        {/* Exchange Requirements Panel */}
        {showRequirements && (
          <div className="p-6 bg-blue-50 border-t border-blue-200">
            <h4 className="text-md font-semibold text-blue-900 mb-3">1031 Exchange Requirements</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span><strong>45-Day Rule:</strong> Identify replacement properties within 45 days</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span><strong>180-Day Rule:</strong> Complete exchange within 180 days</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span><strong>Like-Kind:</strong> Both properties must be held for investment/business</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-blue-600" />
                  <span><strong>Equal/Greater Value:</strong> Replacement must be equal or greater value</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span><strong>Qualified Intermediary:</strong> Required to facilitate the exchange</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <span><strong>Debt Replacement:</strong> Must maintain equal or greater debt levels</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Property Information Input */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Current Property Analysis</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Market Value
            </label>
            <Input
              type="number"
              value={formState.current_market_value}
              onChange={(e) => updateField('current_market_value', e.target.value)}
              placeholder="Current property value"
              error={formState.errors.current_market_value}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Original Purchase Price
            </label>
            <Input
              type="number"
              value={formState.original_purchase_price}
              onChange={(e) => updateField('original_purchase_price', e.target.value)}
              placeholder="Original cost basis"
              error={formState.errors.original_purchase_price}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Depreciation Taken
            </label>
            <Input
              type="number"
              value={formState.total_depreciation_taken}
              onChange={(e) => updateField('total_depreciation_taken', e.target.value)}
              placeholder="Depreciation claimed"
              error={formState.errors.total_depreciation_taken}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capital Improvements Made
            </label>
            <Input
              type="number"
              value={formState.improvement_costs}
              onChange={(e) => updateField('improvement_costs', e.target.value)}
              placeholder="Cost of improvements"
              error={formState.errors.improvement_costs}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selling Costs (%)
            </label>
            <Input
              type="number"
              value={formState.selling_costs_percentage}
              onChange={(e) => updateField('selling_costs_percentage', e.target.value)}
              placeholder="6"
              step="0.1"
              min="0"
              max="15"
              error={formState.errors.selling_costs_percentage}
            />
          </div>
        </div>
      </div>

      {/* Tax Analysis Results */}
      {formState.isCalculating && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
            <span className="ml-3 text-gray-600">Calculating 1031 exchange analysis...</span>
          </div>
        </div>
      )}

      {exchangeAnalysis && !formState.isCalculating && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Tax Impact Analysis</h4>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-medium text-red-600 uppercase tracking-wide">
                Total Tax Liability
              </p>
              <p className="text-2xl font-bold text-red-900">
                ${(exchangeAnalysis.total_tax_liability || 0).toLocaleString()}
              </p>
              <p className="text-xs text-red-600 mt-1">
                If sold without 1031
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm font-medium text-green-600 uppercase tracking-wide">
                Tax Deferred
              </p>
              <p className="text-2xl font-bold text-green-900">
                ${(exchangeAnalysis.tax_deferred_via_1031 || 0).toLocaleString()}
              </p>
              <p className="text-xs text-green-600 mt-1">
                With 1031 exchange
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                Capital Gain
              </p>
              <p className="text-2xl font-bold text-blue-900">
                ${(exchangeAnalysis.potential_capital_gain || 0).toLocaleString()}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Long-term appreciation
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">
                Benefit Percentage
              </p>
              <p className="text-2xl font-bold text-purple-900">
                {isFinite(exchangeBenefitPercentage) ? exchangeBenefitPercentage.toFixed(1) : '0.0'}%
              </p>
              <p className="text-xs text-purple-600 mt-1">
                Of property value
              </p>
            </div>
          </div>

          {/* Detailed Tax Breakdown */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-md font-semibold text-gray-900 mb-3">Tax Breakdown</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Federal Capital Gains Tax:</p>
                <p className="font-semibold text-gray-900">
                  ${((exchangeAnalysis.potential_capital_gain || 0) * (exchangeAnalysis.federal_capital_gains_rate || 0)).toLocaleString()}
                  <span className="text-gray-500 ml-1">({((exchangeAnalysis.federal_capital_gains_rate || 0) * 100).toFixed(1)}%)</span>
                </p>
              </div>
              <div>
                <p className="text-gray-600">State Capital Gains Tax:</p>
                <p className="font-semibold text-gray-900">
                  ${((exchangeAnalysis.potential_capital_gain || 0) * (exchangeAnalysis.state_capital_gains_rate || 0)).toLocaleString()}
                  <span className="text-gray-500 ml-1">({((exchangeAnalysis.state_capital_gains_rate || 0) * 100).toFixed(1)}%)</span>
                </p>
              </div>
              <div>
                <p className="text-gray-600">Depreciation Recapture Tax:</p>
                <p className="font-semibold text-gray-900">
                  ${((exchangeAnalysis.depreciation_recapture || 0) * CAPITAL_GAINS_RATES.depreciation_recapture).toLocaleString()}
                  <span className="text-gray-500 ml-1">(25%)</span>
                </p>
              </div>
              {(exchangeAnalysis.net_investment_income_tax || 0) > 0 && (
                <div>
                  <p className="text-gray-600">Net Investment Income Tax:</p>
                  <p className="font-semibold text-gray-900">
                    ${(exchangeAnalysis.net_investment_income_tax || 0).toLocaleString()}
                    <span className="text-gray-500 ml-1">(3.8%)</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!exchangeAnalysis && !formState.isCalculating && validateBasicInputs() && taxProfile && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <Calculator className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Calculate</h3>
            <p className="text-gray-600 mb-4">
              Your property information is complete. Analysis will automatically calculate.
            </p>
            <Button
              onClick={calculateExchangeAnalysis}
              disabled={formState.isCalculating}
              className="flex items-center gap-2 mx-auto"
            >
              <Calculator className="w-4 h-4" />
              Calculate 1031 Exchange Analysis
            </Button>
          </div>
        </div>
      )}

      {/* Replacement Properties */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Potential Replacement Properties</h4>
          <Button
            variant="secondary"
            size="sm"
            onClick={addReplacementProperty}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Property
          </Button>
        </div>

        {formState.replacement_properties.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ArrowRightLeft className="w-8 h-8 mx-auto mb-2" />
            <p>No replacement properties added</p>
            <p className="text-sm">Add potential replacement properties to analyze exchange feasibility</p>
          </div>
        ) : (
          <div className="space-y-4">
            {formState.replacement_properties.map((prop, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Property Address
                    </label>
                    <Input
                      type="text"
                      value={prop.address}
                      onChange={(e) => updateReplacementProperty(index, 'address', e.target.value)}
                      placeholder="Property address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Market Value
                    </label>
                    <Input
                      type="number"
                      value={prop.market_value.toString()}
                      onChange={(e) => updateReplacementProperty(index, 'market_value', parseFloat(e.target.value) || 0)}
                      placeholder="Property value"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Property Type
                    </label>
                    <select
                      value={prop.property_type}
                      onChange={(e) => updateReplacementProperty(index, 'property_type', e.target.value)}
                      className="w-full px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="industrial">Industrial</option>
                      <option value="land">Land</option>
                    </select>
                  </div>

                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Qualification
                      </label>
                      <select
                        value={prop.qualification_status}
                        onChange={(e) => updateReplacementProperty(index, 'qualification_status', e.target.value as LikeKindProperty['qualification_status'])}
                        className={`w-full px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${getQualificationColor(prop.qualification_status)}`}
                      >
                        <option value="pending_review">Pending Review</option>
                        <option value="qualified">Qualified</option>
                        <option value="not_qualified">Not Qualified</option>
                      </select>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => removeReplacementProperty(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {prop.qualification_notes && (
                  <div className="mt-2">
                    <textarea
                      value={prop.qualification_notes}
                      onChange={(e) => updateReplacementProperty(index, 'qualification_notes', e.target.value)}
                      placeholder="Notes on qualification status"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                )}

                {/* Value Comparison */}
                {exchangeAnalysis && prop.market_value > 0 && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Value Comparison:</span>
                      <span className={`font-medium ${prop.market_value >= (exchangeAnalysis.current_market_value || 0) ? 'text-green-600' : 'text-red-600'}`}>
                        {prop.market_value >= (exchangeAnalysis.current_market_value || 0) ? '✓ Qualifies' : '✗ Too Low'}
                        ({exchangeAnalysis.current_market_value && exchangeAnalysis.current_market_value > 0 ? ((prop.market_value / exchangeAnalysis.current_market_value - 1) * 100).toFixed(1) : '0.0'}%)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Exchange Timeline & Next Steps */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Exchange Timeline & Next Steps</h4>

        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h5 className="text-sm font-semibold text-blue-900">Before Sale (Preparation Phase)</h5>
              <ul className="text-sm text-blue-800 mt-1 space-y-1">
                <li>• Engage a qualified intermediary (QI)</li>
                <li>• Identify potential replacement properties</li>
                <li>• Obtain property valuations and market analysis</li>
                <li>• Review financing options for replacement property</li>
              </ul>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h5 className="text-sm font-semibold text-amber-900">Day 1-45 (Identification Period)</h5>
              <ul className="text-sm text-amber-800 mt-1 space-y-1">
                <li>• Complete sale of relinquished property</li>
                <li>• Identify up to 3 replacement properties (or follow other identification rules)</li>
                <li>• Submit written identification to QI before midnight of day 45</li>
                <li>• Begin due diligence on identified properties</li>
              </ul>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h5 className="text-sm font-semibold text-green-900">Day 46-180 (Exchange Period)</h5>
              <ul className="text-sm text-green-800 mt-1 space-y-1">
                <li>• Complete purchase of replacement property</li>
                <li>• Ensure replacement property value equals or exceeds relinquished property</li>
                <li>• Maintain equal or greater debt on replacement property</li>
                <li>• Close exchange before midnight of day 180</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Important Disclaimers */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-red-800 mb-2">Important Disclaimers</h4>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• This calculator provides estimates only - actual tax implications may vary significantly</li>
              <li>• 1031 exchanges have strict timing and procedural requirements that must be followed exactly</li>
              <li>• Failure to complete a valid exchange will result in immediate tax recognition of gains</li>
              <li>• Consult with qualified intermediary, tax professional, and real estate attorney before proceeding</li>
              <li>• State laws and regulations may impose additional requirements or restrictions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}